import type { CardPhotoSlot } from '../photo-slots'
import type { CardKind, GenerationCardSettings, TrendMemberPreview } from '../types/generationCard'

export type ModelCategory = 'text' | 'image' | 'video' | 'audio' | 'presentation'
export type TaskType = 'text' | 't2i' | 'i2i' | 't2v' | 'i2v' | 'avatar' | 'motion' | 't2a'

export interface Model {
	id: string
	provider: string
	name: string
	shortName: string
	description: string
	category: ModelCategory
	taskType: TaskType
	cost: number
	// defaultCost — «полная» цена модели (credits_price из БД), используется как
	// fallback в computeModelCost когда выбранные settings НЕ матчат ни один variant.
	// Отличается от `cost`: cost = min_credits_price для UI-надписи «от X».
	// Например gpt-image-2: cost=7 (low variant) / defaultCost=50 (medium/main).
	defaultCost?: number
	/** per_input_image — надбавка за каждое приложенное фото (строка «input_photo» модели), не цена генерации. */
	variants?: { key: string; label: string; credits_price: number | null; is_per_second?: boolean; per_input_image?: boolean }[]
	/** Плата за секунды входного видео включена (выключатель «Правила цены» в админке, backend services/input_video_billing):
	 *  с видео на входе цена — строка «<разрешение>_video_io» × (секунды входа + секунды результата). */
	inputVideoBilling?: boolean
	rating: number
	users: number
	icon: string
	darkIcon?: boolean
	color: string
	backendModel: string
	adapter?: string
	requiresImage?: boolean
	supportsImageInput?: boolean
	supportsNegativePrompt?: boolean
	supportsOutputFormat?: boolean
	/** Какие форматы файла модель принимает (из кода адаптера или ui_metadata).
	 *  Без списка — общий набор JPG/PNG (+WebP при supportsOutputFormat). */
	outputFormats?: string[]
	allowedOutputFormats?: string[]
	supportsGoogleSearch?: boolean
	supportsPromptOptimizer?: boolean
	qualityLevels?: string[]
	requiresTwoImages?: boolean
	requiresVideo?: boolean
	aspectRatios?: string[]
	resolutions?: string[]
	durations?: string[]
	/** Секунды, которые принимает сама модель (код адаптера, до сужения списком админа): по ним
	 *  сервер считает длину и цену «Переделать видео» по референсу — виджет и узел режут так же. */
	providerDurationRange?: [number, number]
	maxImages?: number
	supportsAudio?: boolean
	supportsEndImage?: boolean
	supportsReferenceImages?: boolean
	maxReferenceImages?: number
	supportsReferenceVideo?: boolean
	/** Намерение при видео-референсе («Переделать видео | Новое по референсу») — Seedance 2.5. */
	supportsReferenceVideoIntent?: boolean
	/** Как модель называет материалы в промпте (@Image1 у Seedance, @image_1 у Kling Omni) — подписи пилюль. */
	promptRefStyle?: 'seedance' | 'kling' | 'wan'
	supportsMultiShot?: boolean
	supportsLastFrame?: boolean
	supportsReferenceAudio?: boolean
	supportsGenerateAudio?: boolean
	supportsWebSearch?: boolean
	supportsNsfwChecker?: boolean
	maxReferenceVideos?: number
	/** Короче этого видео-референс модель не принимает (Kling: 3 с). Длинные
	 *  бэк режет сам, а удлинить нечем — проверяем до загрузки. */
	minReferenceVideoSeconds?: number
	maxReferenceAudios?: number
	/** Суммарный потолок видео-референсов, с (из кода адаптера: Seedance 2.5 — 30, 2.0 — 15).
	 *  Длинные ролики бэк подрежет сам; без числа виджет считает 15. */
	maxReferenceVideoSeconds?: number
	/** Суммарный потолок аудио-референсов, с; один файл длиннее — тример. Без числа — 15. */
	maxReferenceAudioSeconds?: number
	/** Рамки фото на входе у провайдера (стороны в px и максимум вытянутости) — проверка
	 *  до загрузки, чтобы провайдер не отклонял после списания. */
	imageLimits?: { minPx: number; maxPx: number; maxRatio: number }
	/** Форматы файлов, которые читает провайдер (Seedance: видео mp4/mov, аудио mp3/wav) —
	 *  фильтр в окне выбора файла, проверка до загрузки и подсказка у поля. */
	referenceVideoFormats?: string[]
	referenceAudioFormats?: string[]
	/** Потолок размера файла у провайдера, МБ (сайт ужимает своим лимитом, если он ниже). */
	referenceVideoMaxMb?: number
	referenceAudioMaxMb?: number
	/** Продление сцены: у готового ролика есть «Продлить» — модель дописывает
	 *  хвост к СВОЕЙ же генерации (Gemini Omni Original, `previous_interaction_id`). */
	supportsSceneExtend?: boolean
	/** Потолок накопленной сцены при продлении, сек. */
	maxSceneSeconds?: number
	/** Подсказки к параметрам из ui_metadata: подпись и описание поля. */
	paramHints?: Record<string, { label?: string; description?: string }>
	/** Модель не читает текст вовсе (например face-swap: промпт отбрасывается).
	 *  Приходит из ui_metadata; поле промпта у такой модели не показываем. */
	noPrompt?: boolean
	/** «Для чего эта нейронка» — бэк выводит из реальных возможностей модели. */
	scenarios?: { key: string; label: string; detail: string }[]
	/** «Как встанет персонаж» — вердикт резолвера бэка (см. utils/characterFit). */
	characterFit?: { mode: string; count?: number; warnings?: string[] } | null
	/** Карточка-инструмент каталога (таблица catalog_tools), а не модель адаптера.
	 *  Клик по ней НЕ выбирает модель, а уводит на toolUrl. Такие элементы живут
	 *  только в витринах (дропдаун «Все нейросети» + /bots) и намеренно НЕ входят
	 *  в allModels/widgetModels — иначе попали бы в пикеры генерации. */
	isTool?: boolean
	/** Внутренний путь SPA, куда ведёт карточка-инструмент. */
	toolUrl?: string
	/** Готовая подпись цены инструмента («от 50»); пусто — блок цены не рисуем. */
	priceLabel?: string
	/** Позиция в витрине (model_catalog.sort_order / catalog_tools.sort_order —
	 *  общая шкала для моделей и инструментов). Нужна витринам, чтобы склеить
	 *  два списка в один порядок, заданный drag-сортировкой в админке. */
	showcaseOrder?: number
}

export const MODELS: Model[] = [
	{
		id: 'gpt-5.2',
		provider: 'openai',
		name: 'GPT 5.2',
		shortName: '5.2',
		description: 'Новейшая модель от OpenAI для текста и изображений',
		category: 'text',
		taskType: 'text',
		cost: 30,
		rating: 4.6,
		users: 194052,
		icon: '/icons/gpt.svg',
		color: '#10a37f',
		backendModel: 'gpt-4o',
	},
	{
		id: 'claude-sonnet-4.5',
		provider: 'anthropic',
		name: 'Claude Sonnet 4.5',
		shortName: 'Sonnet 4.5',
		description: 'Продвинутая модель от Anthropic для сложных задач',
		category: 'text',
		taskType: 'text',
		cost: 25,
		rating: 4.83,
		users: 168041,
		icon: '/icons/claude.svg',
		color: '#d97706',
		backendModel: 'claude-sonnet-4-5-20250929',
	},
	{
		id: 'gemini-3-pro',
		provider: 'gemini',
		name: 'Gemini 3 Pro',
		shortName: '3 Pro',
		description: 'Мощная мультимодальная модель от Google',
		category: 'text',
		taskType: 'text',
		cost: 20,
		rating: 4.6,
		users: 287571,
		icon: '/icons/gemini.svg',
		color: '#4285f4',
		backendModel: 'gemini-2.0-flash',
	},
	{
		id: 'gemini-3-flash',
		provider: 'gemini',
		name: 'Gemini 3 Flash',
		shortName: '3 Flash',
		description: 'Быстрая модель от Google для простых задач',
		category: 'text',
		taskType: 'text',
		cost: 5,
		rating: 4.67,
		users: 281434,
		icon: '/icons/gemini.svg',
		color: '#4285f4',
		backendModel: 'gemini-1.5-flash',
	},
	{
		id: 'deepseek-3.2-chat',
		provider: 'deepseek',
		name: 'DeepSeek 3.2 Chat',
		shortName: '3.2 Chat',
		description: 'Бесплатная модель для быстрых ответов',
		category: 'text',
		taskType: 'text',
		cost: 0,
		rating: 4.93,
		users: 72596,
		icon: '/icons/deepsek.svg',
		color: '#6366f1',
		backendModel: 'deepseek-chat',
	},
	{
		id: 'grok',
		provider: 'xai',
		name: 'Grok',
		shortName: '',
		description: 'Модель от xAI с доступом к актуальной информации',
		category: 'text',
		taskType: 'text',
		cost: 15,
		rating: 4.6,
		users: 276269,
		icon: '/icons/grok.svg',
		color: '#1d1d1f',
		backendModel: 'grok-3-mini',
	},
	{
		id: 'nanobanana-pro',
		provider: 'nano_banana',
		name: 'Nano Banana Pro',
		shortName: 'Nano Banana Pro',
		description: 'Лучшая нейросеть для генерации изображений',
		category: 'image',
		taskType: 't2i',
		cost: 55,
		rating: 4.84,
		users: 241528,
		icon: '/icons/nanobanano.svg',
		color: '#eab308',
		backendModel: 'nano-banana-pro',
		aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4'],
		resolutions: ['1K', '2K', '4K'],
	},
	{
		id: 'nanobanana-standard',
		provider: 'nano_banana',
		name: 'Nano Banana Standard',
		shortName: 'Nano Banana Standard',
		description: 'Базовая версия генератора изображений',
		category: 'image',
		taskType: 't2i',
		cost: 25,
		rating: 4.6,
		users: 98454,
		icon: '/icons/nanobanano.svg',
		color: '#eab308',
		backendModel: 'google/nano-banana',
		aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4'],
		resolutions: ['1K', '2K'],
	},
	{
		id: 'midjourney-t2i',
		provider: 'midjourney',
		name: 'Midjourney Text to Image',
		shortName: 'Text to Image',
		description: 'Генерация изображений по текстовому описанию',
		category: 'image',
		taskType: 't2i',
		cost: 30,
		rating: 4.79,
		users: 201176,
		icon: '/icons/midjournet.svg',
		color: '#0d9488',
		backendModel: 'mj_txt2img',
		aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'],
	},
	{
		id: 'midjourney-i2i',
		provider: 'midjourney',
		name: 'Midjourney Image to Image',
		shortName: 'Image to Image',
		description: 'Преобразование и стилизация изображений',
		category: 'image',
		taskType: 'i2i',
		cost: 35,
		rating: 4.58,
		users: 247731,
		icon: '/icons/midjournet.svg',
		color: '#0d9488',
		backendModel: 'mj_img2img',
		requiresImage: true,
		aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'],
	},
	{
		id: 'flux',
		provider: 'flux',
		name: 'Flux',
		shortName: 'Flux 2 Pro',
		description: 'Генерация изображений высокого качества',
		category: 'image',
		taskType: 't2i',
		cost: 25,
		rating: 4.52,
		users: 145757,
		icon: '/icons/flux.svg',
		color: '#8b5cf6',
		backendModel: 'flux-2/pro-text-to-image',
		aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'],
		resolutions: ['1K', '2K'],
	},
	{
		id: 'midjourney-i2v',
		provider: 'midjourney',
		name: 'Midjourney Image to Video',
		shortName: 'Image to Video',
		description: 'Создание видео из статичных изображений',
		category: 'video',
		taskType: 'i2v',
		cost: 100,
		rating: 4.56,
		users: 63368,
		icon: '/icons/midjournet.svg',
		color: '#0d9488',
		backendModel: 'mj_video',
		requiresImage: true,
	},
	{
		id: 'kling-avatar-pro',
		provider: 'kling',
		name: 'Kling Avatar Pro',
		shortName: 'Kling Avatar Pro',
		description: 'Создание реалистичных аватаров с анимацией',
		category: 'video',
		taskType: 'avatar',
		cost: 150,
		rating: 4.71,
		users: 116772,
		icon: '/icons/faceswap.svg',
		color: '#22c55e',
		backendModel: 'kling/ai-avatar-pro',
		requiresImage: true,
	},
	{
		id: 'kling-2.6-i2v',
		provider: 'kling',
		name: 'Kling 2.6 Image to Video',
		shortName: 'Image to Video',
		description: 'Генерация видео из изображений нового поколения',
		category: 'video',
		taskType: 'i2v',
		cost: 200,
		rating: 4.56,
		users: 74974,
		icon: '/icons/kling.svg',
		color: '#22c55e',
		backendModel: 'kling-2.6/image-to-video',
		requiresImage: true,
		aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4'],
		durations: ['5', '10'],
	},
	{
		id: 'kling-t2v',
		provider: 'kling',
		name: 'Kling Text to Video',
		shortName: 'Text to Video',
		description: 'Создание видео по текстовому описанию',
		category: 'video',
		taskType: 't2v',
		cost: 250,
		rating: 4.94,
		users: 47508,
		icon: '/icons/kling.svg',
		color: '#22c55e',
		backendModel: 'kling-2.6/text-to-video',
		aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4'],
		durations: ['5', '10'],
	},
	{
		id: 'kling-motion-control',
		provider: 'kling',
		name: 'Kling Motion Control',
		shortName: 'Motion Control',
		description: 'Перенос движений на фото и видео',
		category: 'video',
		taskType: 'motion',
		cost: 400,
		rating: 4.94,
		users: 278117,
		icon: '/icons/KlingMotion.svg',
		color: '#22c55e',
		backendModel: 'kling-2.6/motion-control',
		requiresImage: true,
		requiresVideo: true,
		supportsReferenceVideo: true,
		durations: ['5', '10'],
	},
	{
		id: 'kling-v3-video',
		provider: 'replicate',
		name: 'Kling v3',
		shortName: 'v3',
		description: 'Новейшая модель Kling v3 с поддержкой аудио и 1080p',
		category: 'video',
		taskType: 't2v',
		cost: 300,
		rating: 4.97,
		users: 1016,
		icon: '/icons/kling.svg',
		color: '#22c55e',
		backendModel: 'kwaivgi/kling-v3-video',
		supportsImageInput: true,
		aspectRatios: ['16:9', '9:16', '1:1'],
		durations: ['3', '5', '10', '15'],
	},
	{
		// Kling 3.0 напрямую от разработчика. Вторая карточка рядом с
		// KIE-версией, а не замена ей: обе показываются одновременно
		// намеренно (прецедент — Gemini Omni (Original)). Оригинал дороже,
		// но без очередей и модерации посредника.
		id: 'kling-3.0-direct',
		provider: 'kling_direct',
		name: 'Kling 3.0 (Original)',
		shortName: 'Kling 3.0 (Original)',
		description: 'Kling 3.0 напрямую от разработчика: без очередей и ограничений посредника. Видео со звуком, до 4K.',
		category: 'video',
		taskType: 't2v',
		cost: 300,
		rating: 4.97,
		users: 0,
		icon: '/icons/kling.svg',
		color: '#22c55e',
		backendModel: 'kling-3.0-direct',
		supportsImageInput: true,
		// Первый кадр — загруженное фото, последний — общий слот «конец видео»
		// (у Kling это image_tail).
		supportsEndImage: true,
		aspectRatios: ['16:9', '9:16', '1:1'],
		durations: ['3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'],
		resolutions: ['720p', '1080p', '2160p'],
	},
	{
		// Kling V3 Omni напрямую. Мультимодальная: кроме кадров принимает
		// фото-референсы, видео и аудио. До сих пор Omni был доступен только
		// через Replicate — прямая версия живёт под своим id и показывается
		// рядом с ней, как 3.0 рядом с KIE-версией.
		id: 'kling-v3-omni-direct',
		provider: 'kling_direct',
		name: 'Kling V3 Omni (Original)',
		shortName: 'Omni (Original)',
		description: 'Kling V3 Omni напрямую от разработчика: видео со звуком по фото, видео- и аудио-референсам.',
		category: 'video',
		taskType: 't2v',
		cost: 400,
		rating: 4.95,
		users: 0,
		icon: '/icons/kling.svg',
		color: '#22c55e',
		backendModel: 'kling-v3-omni-direct',
		supportsImageInput: true,
		supportsEndImage: true,
		supportsReferenceImages: true,
		supportsReferenceVideo: true,
		maxReferenceVideos: 1,
		minReferenceVideoSeconds: 3,
		maxReferenceImages: 7,
		aspectRatios: ['16:9', '9:16', '1:1'],
		durations: ['3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'],
		resolutions: ['720p', '1080p', '2160p'],
	},
	{
		id: 'veo-t2v',
		provider: 'veo',
		name: 'Google Veo',
		shortName: 'Google Veo',
		description: 'Генерация видео от Google DeepMind',
		category: 'video',
		taskType: 't2v',
		cost: 200,
		rating: 4.55,
		users: 290285,
		icon: '/icons/veo.svg',
		color: '#ea4335',
		backendModel: 'veo-2.0-generate-001',
		aspectRatios: ['16:9', '9:16'],
		durations: ['5', '8'],
	},
	{
		id: 'sora-t2v',
		provider: 'sora',
		name: 'Sora',
		shortName: 'Sora',
		description: 'Генерация видео от OpenAI',
		category: 'video',
		taskType: 't2v',
		cost: 300,
		rating: 4.98,
		users: 88188,
		icon: '/icons/sora.svg',
		color: '#10a37f',
		backendModel: 'sora-2.0',
		aspectRatios: ['16:9', '9:16', '1:1'],
		durations: ['5', '10'],
	},
	{
		id: 'hailuo-t2v',
		provider: 'hailuo',
		name: 'Hailuo AI',
		shortName: '',
		description: 'Качественная генерация видео от MiniMax',
		category: 'video',
		taskType: 't2v',
		cost: 180,
		rating: 4.72,
		users: 182501,
		icon: '/icons/hailuo.svg',
		color: '#3b82f6',
		backendModel: 'hailuo/t2v',
		aspectRatios: ['16:9', '9:16'],
		durations: ['6', '10'],
	},
	{
		id: 'hailuo-02-t2v-standard',
		provider: 'hailuo',
		name: 'Hailuo 02 T2V Standard',
		shortName: 'T2V Standard',
		description: 'Стандартная генерация видео из текста',
		category: 'video',
		taskType: 't2v',
		cost: 35,
		rating: 4.65,
		users: 250597,
		icon: '/icons/hailuo.svg',
		color: '#3b82f6',
		backendModel: 'hailuo/02-text-to-video-standard',
		supportsPromptOptimizer: true,
		aspectRatios: ['16:9', '9:16', '1:1'],
		durations: ['6', '10'],
	},
	{
		id: 'hailuo-02-t2v-pro',
		provider: 'hailuo',
		name: 'Hailuo 02 T2V Pro',
		shortName: 'T2V Pro',
		description: 'Профессиональная генерация видео из текста',
		category: 'video',
		taskType: 't2v',
		cost: 35,
		rating: 4.71,
		users: 106532,
		icon: '/icons/hailuo.svg',
		color: '#3b82f6',
		backendModel: 'hailuo/02-text-to-video-pro',
		supportsPromptOptimizer: true,
		aspectRatios: ['16:9', '9:16', '1:1'],
		durations: ['6', '10'],
	},
	{
		id: 'hailuo-02-i2v-standard',
		provider: 'hailuo',
		name: 'Hailuo 02 I2V Standard',
		shortName: 'I2V Standard',
		description: 'Стандартная генерация видео из изображения',
		category: 'video',
		taskType: 'i2v',
		cost: 35,
		rating: 4.83,
		users: 96816,
		icon: '/icons/hailuo.svg',
		color: '#3b82f6',
		backendModel: 'hailuo/02-image-to-video-standard',
		requiresImage: true,
		supportsPromptOptimizer: true,
		aspectRatios: ['16:9', '9:16', '1:1'],
		durations: ['6', '10'],
	},
	{
		id: 'hailuo-02-i2v-pro',
		provider: 'hailuo',
		name: 'Hailuo 02 I2V Pro',
		shortName: 'I2V Pro',
		description: 'Профессиональная генерация видео из изображения',
		category: 'video',
		taskType: 'i2v',
		cost: 35,
		rating: 4.94,
		users: 270586,
		icon: '/icons/hailuo.svg',
		color: '#3b82f6',
		backendModel: 'hailuo/02-image-to-video-pro',
		requiresImage: true,
		supportsPromptOptimizer: true,
		aspectRatios: ['16:9', '9:16', '1:1'],
		durations: ['6', '10'],
	},
	{
		id: 'hailuo-02-fast',
		provider: 'hailuo',
		name: 'Hailuo 02 Fast',
		shortName: 'Fast',
		description: 'Быстрая генерация видео',
		category: 'video',
		taskType: 't2v',
		cost: 105,
		rating: 4.64,
		users: 212042,
		icon: '/icons/hailuo.svg',
		color: '#3b82f6',
		backendModel: 'hailuo/02-text-to-video-fast',
		supportsPromptOptimizer: true,
		aspectRatios: ['16:9', '9:16', '1:1'],
		durations: ['6', '10'],
	},
	{
		id: 'runway-t2v',
		provider: 'runway',
		name: 'Runway Gen-3',
		shortName: 'Gen-3',
		description: 'Профессиональная генерация видео',
		category: 'video',
		taskType: 't2v',
		cost: 250,
		rating: 4.56,
		users: 82797,
		icon: '/icons/runway.svg',
		color: '#6d28d9',
		backendModel: 'runway/gen3a_turbo',
		aspectRatios: ['16:9', '9:16'],
		durations: ['5', '10'],
	},
	{
		id: 'seedance-t2v',
		provider: 'seedance',
		name: 'Seedance',
		shortName: 'Seedance',
		description: 'Генерация танцевальных и динамичных видео',
		category: 'video',
		taskType: 't2v',
		cost: 200,
		rating: 4.73,
		users: 222860,
		icon: '/icons/seedance.svg',
		color: '#059669',
		backendModel: 'seedance-1.0/t2v',
		aspectRatios: ['16:9', '9:16', '1:1'],
		durations: ['5'],
	},
	{
		// Fallback-запись каталога. Основной источник — backend /providers
		// (модель авто-появляется по model_provider_prices.ui_metadata +
		// model_settings). Пиллы/цена/референсы приходят из ui_metadata и
		// MODEL_METADATA в useProviders. Аудио генерируется моделью (без
		// тумблера звука). Референсов до 7 (omni-uploader).
		id: 'gemini-omni-video',
		provider: 'kie',
		name: 'Gemini Omni',
		shortName: 'Omni',
		description: 'Мультимодальная видеогенерация Google: видео со звуком из текста и референсов, до 4k',
		category: 'video',
		taskType: 't2v',
		cost: 400,
		rating: 4.9,
		users: 0,
		icon: '/icons/veo.svg',
		color: '#4285f4',
		backendModel: 'gemini-omni-video',
		supportsImageInput: true,
		supportsReferenceImages: true,
		maxReferenceImages: 7,
		supportsReferenceVideo: true,
		maxReferenceVideos: 1,
		aspectRatios: ['16:9', '9:16'],
		durations: ['4', '6', '8', '10'],
		resolutions: ['720p', '1080p', '4k'],
	},
	{
		// Прямая версия той же модели (Gemini Developer API, без посредника):
		// самостоятельная карточка — дороже, но без ограничений провайдера
		// (разницу объясняют щиты ограничений). С 03.09.2026 под капотом
		// gemini-omni-1.1-flash: 1080p/4k появились и здесь, потолок 720p
		// был ограничением снятой preview-версии, а не выбором витрины.
		id: 'gemini-omni-video-direct',
		provider: 'google',
		name: 'Gemini Omni (Original)',
		shortName: 'Omni (Original)',
		description: 'Gemini Omni напрямую от Google: без ограничений посредника. Видео со звуком из текста и референсов, до 4k.',
		category: 'video',
		taskType: 't2v',
		cost: 450,
		rating: 4.9,
		users: 0,
		icon: '/icons/veo.svg',
		color: '#4285f4',
		backendModel: 'gemini-omni-video-direct',
		supportsImageInput: true,
		supportsReferenceImages: true,
		maxReferenceImages: 6,
		// Видео-референсы 1.1: до трёх по 3 секунды, адаптер заливает их в
		// Google Files API. Кадры — через общий слот «конец видео»
		// (supportsEndImage): начальная картинка + конечная = <FIRST_FRAME>
		// и <LAST_FRAME>.
		supportsReferenceVideo: true,
		maxReferenceVideos: 3,
		supportsEndImage: true,
		supportsSceneExtend: true,
		maxSceneSeconds: 40,
		aspectRatios: ['16:9', '9:16'],
		durations: ['4', '6', '8', '10'],
		resolutions: ['720p', '1080p', '4k'],
	},
	{
		id: 'gamma-presentation',
		provider: 'gamma',
		name: 'Gamma AI',
		shortName: 'Gamma',
		description: 'Создание новых презентаций с профессиональным дизайном и AI-изображениями.',
		category: 'presentation',
		taskType: 'text',
		cost: 200,
		rating: 4.9,
		users: 0,
		icon: '/icons/gamma.svg',
		color: '#8b5cf6',
		backendModel: 'gamma-presentation',
	},
	{
		id: 'umnik-slides',
		provider: 'anthropic',
		name: 'Umnik AI',
		shortName: 'Umnik AI',
		description: 'AI-помощник для презентаций. Загрузите PPTX и опишите изменения.',
		category: 'presentation',
		taskType: 'text',
		cost: 50,
		rating: 4.8,
		users: 0,
		icon: '/icons/umnik-slides.svg',
		color: '#6366f1',
		backendModel: 'umnik-slides',
	},
]

export type PopularModelCard = {
	/** Слаг карточки-инструмента — SEO-URL /bots/{раздел}/tool/{slug} и якорь
	 *  лендинга (data-card-slug на плитке). */
	slug?: string
	id: string
	badge?: string
	/** 'droplet' — доп-стиль плитки «стекло-капля» (задаёт админ, см. DropletGlass). */
	tileStyle?: string
	title: string
	description: string
	rating: number
	reviews: number
	modelLogoUrl: string
	creditsLabel: string
	backgroundImageUrl: string
	/** Webp-превью фона ≤512px (производный URL с бэка). Грузим его вместо
	 *  оригинала; если объекта ещё нет (бэкфил не прогнан) — onError-фолбэк. */
	backgroundPreviewUrl?: string | null
	backgroundColor?: string
	targetAdapter?: string
	targetModelId?: string
	defaultPrompt?: string
	// Пресет параметров генерации с карточки popular_category (применяется
	// при клике → /generate, см. SelectedModelContext.pendingSettings).
	settings?: GenerationCardSettings
	// Число использований (суточный снимок card_usage, обогащается на бэке).
	usedCount?: number
	// Видео-превью (video-карточки) / аудио (audio-карточки) — играет поверх постера.
	videoUrl?: string
	/** Постер видео = его первый кадр (`<key>_preview.webp`, кладёт upload-video —
	 *  та же конвенция, что у превью обложек). Нужен карточкам БЕЗ обложки: без
	 *  него плитка зависела от того, решит браузер декодировать кадр или нет.
	 *  Едет атрибутом poster, поэтому отсутствие файла ничего не ломает. */
	videoPosterUrl?: string
	/** Лёгкий беззвучный ролик (`<key>_loop.mp4`, длинная сторона 640) — именно он
	 *  играет на плитке при автоплее; оригинал остаётся для полноэкранного
	 *  просмотра и как фолбэк, пока соседа нет (см. utils/cardVideoAutoplay). */
	videoLoopUrl?: string
	audioUrl?: string
	/** Видео-референс «под капотом» (video-модели): авто-подставляется в слот
	 *  «Ваше видео» инструмента — юзер добавляет только фото. */
	/** Фото «под капотом» карточки: уходит в первый слот вместо юзера. */
	referenceImageUrl?: string
	userImageLocked?: boolean
	/** Референс есть, ссылки у клиента нет (19.09) — см. api/cardReference. */
	hasRefVideo?: boolean
	/** Длина видео тренда по замеру сервера (reference_video_seconds) — в пин: цена гостю как на плитке. */
	refVideoSeconds?: number | null
	hasRefAudio?: boolean
	referenceVideoUrl?: string
	/** true = слот «Ваше видео» скрыт: генерация всегда с нашим референсом. */
	userVideoLocked?: boolean
	/** Аудио-референс «под капотом» (модели с аудио-каналом) + лок аудио-ввода. */
	referenceAudioUrl?: string
	userAudioLocked?: boolean
	/** «Своя цена карточки»: floor-оверрайд в кредитах (бэк спишет max(base, override)). */
	priceOverride?: number
	/** Активная акция: % и остаток срока для плашки (priceOverride уже скидочный). */
	discountPercent?: number
	discountLeftLabel?: string
	/** Конец акции (ISO) — едет в пин: живость промо проверяется на расчёте. */
	discountUntil?: string
	/** Обычная цена ДО акции — зачёркнутый ценник промо-стикера плитки
	 *  и гейт промо в виджете (см. GenerationCard.regularPrice). */
	creditsOldLabel?: string
	regularPrice?: number
	/** Пайплайн-карточка: slug шаблона (тренды «фото → видео» в GTM). */
	pipelineSlug?: string
	/** Наглядное демо карточки (ba «до/после» | video) — уезжает в пин →
	 *  демо-зона GTM. Без этих полей клик с главной открывал инструмент
	 *  с ПУСТОЙ демо-зоной (в отличие от /bots, где поля мапились). */
	demoType?: 'ba' | 'video'
	demoBeforeUrl?: string
	/** Пара «до/после» без вариаций — наглядный слайдер без стилей. */
	demoAfterUrl?: string
	demoVariants?: Array<{ key: string; label: string; after_url: string; prompt_patch: string; is_open?: boolean; placeholder?: string; description?: string; value_type?: 'text' | 'number'; min_value?: number | null; max_value?: number | null; user_input?: { label?: string; placeholder?: string; type?: 'text' | 'number'; min?: number | null; max?: number | null } | null }>
	/** Мультистиль: одна карточка с вариациями-стилями + карточное «поле
	 *  юзера» поверх выбора стиля ({value} в патчах). Плитка носит бейдж
	 *  «N стилей» с мини-превью. */
	cardKind?: CardKind
	/** Мульти-тренд: участники для бейджа «N трендов» и свотчей плитки. */
	trendMembers?: TrendMemberPreview[]
	/** Адрес лендинга карточки (/bots/<раздел>/tool/<slug>); группа трендов открывается только там. */
	primaryUrl?: string | null
	/** Карточка из шаблона флоу: шаблон, шаги и цена «от» (клик открывает шаблон). */
	flowTemplateId?: string
	flowSteps?: number
	flowPriceFrom?: number | null
	flowCoverPosition?: string | null
	userInput?: { label?: string; placeholder?: string; type?: 'text' | 'number'; min?: number | null; max?: number | null } | null
	/** Именованные фото-слоты юзера (подписи ячеек ввода). */
	photoSlots?: CardPhotoSlot[] | null
	hideCharacter?: boolean
	userPhotoLimit?: number
	userPhotoHint?: string
	hideEndImage?: boolean
	endImageHint?: string
	characterHint?: string
	/** Гость: готовый промпт вырезан бэком → GTM показывает CTA. */
	promptLocked?: boolean
	disabled?: boolean
}

// Хардкод-массив `popularModels` удалён — все «Популярные» карточки
// теперь приходят с бэка через `/api/v1/admin/page-sections/public/page/:slug`
// (section_type='popular_category' с config.cards). Тип PopularModelCard
// (выше) сохранён — его используют context/hooks/UI-компоненты для
// типизации динамических данных.

export const CATEGORIES = [
	{ id: 'all', name: 'Все' },
	{ id: 'text', name: 'Текст' },
	{ id: 'image', name: 'Изображения' },
	{ id: 'video', name: 'Видео' },
	{ id: 'audio', name: 'Аудио' },
	{ id: 'presentation', name: 'Презентации' },
]

export const getModelsByCategory = (category: ModelCategory | 'all') => {
	if (category === 'all') return MODELS
	return MODELS.filter((model) => model.category === category)
}

export const getPopularModels = () => {
	return [...MODELS].sort((a, b) => b.users - a.users).slice(0, 6)
}
